import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { CreatePrintDocumentDto } from 'src/app/models/print-documents/create-print-document.dto';
import { DocumentCategory, PrintDocument } from 'src/app/models/print-documents/print-document.interface';
import { CompaniesService } from 'src/app/services/companies.service';
import { PrintDocumentsService } from 'src/app/services/print-documents.service';
import { WordTemplate } from 'src/app/services/word-templates/word-template.model';
import { WordTemplatesService } from 'src/app/services/word-templates/word-templates.service';
import { FormUtil, FormOutType } from 'src/app/utils/form.util';

/**
 * Component for viewing a print document.
 */
@Component({
    selector: 'app-print-document-detail',
    templateUrl: './print-document-detail.component.html',
    styleUrls: ['./print-document-detail.component.scss'],
    standalone: false,
})
export class PrintDocumentDetailComponent implements OnInit, OnDestroy {
    /**
     * The print document id
     */
    @Input() id?: string;
    /**
     * Whether to add the content container class
     */
    @Input() isInProjectDetail = false;
    /**
     * Emitted when the document is replaced and saved
     */
    @Output() save = new EventEmitter<PrintDocument>();
    /**
     * Emitted when the drive item is changed
     */
    @Output() driveItemChange = new EventEmitter<void>();

    headline = '';
    newDocument = false;

    form = new UntypedFormGroup({
        name: new UntypedFormControl('', [Validators.required]),
        category: new UntypedFormControl(
            this.isInProjectDetail ? DocumentCategory.THANK_YOU_LETTER : DocumentCategory.FORM_LETTER,
            [Validators.required],
        ),
    });
    hasChanges = false;
    activeRequest = false;

    wordTemplate?: WordTemplate;

    document?: PrintDocument;

    documentCategory = DocumentCategory;

    private readonly formUtil = new FormUtil(this.form);
    private readonly destroy = new Subject<void>();

    constructor(
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly documentsService: PrintDocumentsService,
        private readonly wordTemplatesService: WordTemplatesService,
    ) {}

    ngOnInit(): void {
        const id = this.id ?? this.route.snapshot.paramMap.get('id');
        this.newDocument = id === 'neu';

        this.form.valueChanges.pipe(takeUntil(this.destroy)).subscribe(() => {
            this.hasChanges = true;
        });

        if (!this.newDocument && id) {
            this.documentsService.findOne(id).subscribe((res) => {
                // set category for old documents
                if (res.categoryName) {
                    res.category =
                        res.categoryName === 'Dankeschreiben'
                            ? DocumentCategory.THANK_YOU_LETTER
                            : DocumentCategory.FORM_LETTER;
                }

                this.formUtil.setValues(res);
                this.document = res;

                this.headline = res.name;

                if (res.file?.id) {
                    this.wordTemplatesService.findAll().subscribe((wordTemplates) => {
                        this.wordTemplate = wordTemplates.find((template) => template._id === res.file?.id);
                    });
                }
            });
        } else {
            this.headline = 'Neues Dokument';
        }
    }

    ngOnDestroy(): void {
        this.destroy.next();
        this.destroy.complete();
    }

    close(): void {
        this.router.navigate(['/einstellungen/dokumente']);
    }

    wordTemplateChanged(wordTemplate: WordTemplate): void {
        this.hasChanges = true;
        this.wordTemplate = wordTemplate;
        this.driveItemChange.emit();
    }

    onSubmit(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        this.activeRequest = true;
        this.hasChanges = false;

        const dto: CreatePrintDocumentDto = {
            name: this.formUtil.getValue({ key: 'name', outType: FormOutType.STRING }),
            category: this.formUtil.getValue({ key: 'category', outType: FormOutType.STRING }),
        };

        if (this.wordTemplate) {
            dto.file = {
                id: this.wordTemplate._id,
                name: this.wordTemplate.name,
            };
        }

        if (this.document) {
            this.documentsService.update({ id: this.document._id, dto }).subscribe(
                (res: PrintDocument) => {
                    this.activeRequest = false;
                    this.save.emit(res);
                    if (!this.id) {
                        this.close();
                    }
                },
                () => {
                    this.activeRequest = false;
                },
            );
        } else {
            this.documentsService.create(dto).subscribe(
                (res: PrintDocument) => {
                    this.activeRequest = false;
                    this.save.emit(res);
                    if (!this.id) {
                        this.close();
                    }
                },
                () => {
                    this.activeRequest = false;
                },
            );
        }
    }

    onSend(): Observable<PrintDocument> {
        const dto: CreatePrintDocumentDto = {
            name: this.formUtil.getValue({ key: 'name', outType: FormOutType.STRING }),
            category: this.formUtil.getValue({ key: 'category', outType: FormOutType.STRING }),
        };

        if (this.wordTemplate) {
            dto.file = {
                id: this.wordTemplate._id,
                name: this.wordTemplate.name,
            };
        }

        if (this.document) {
            return this.documentsService.update({ id: this.document._id, dto });
        } else {
            return this.documentsService.create(dto).pipe(
                tap((res) => {
                    this.document = res;
                }),
            );
        }
    }
}
